<script setup>
import { ref, defineProps, defineModel, computed } from "vue";
import AppIcon from "./AppIcon.vue";
import { load } from "webfontloader";
import VSelectSearch from "@/components/VSelectSearch.vue";
import AppRuleVersionPicker from "./AppRuleVersionPicker.vue";

const inheritAppRules = defineModel("inheritAppRules");
const model = defineModel("rules");
const props = defineProps({
  apps: Array,
  loading: Boolean,
  group: Boolean,
  readOnlyMode: Boolean,
});

const headers = [
  { title: "Application", value: "name", sortable: true },
  { title: "Architecture", value: "stream", sortable: true },
  {
    title: "Patch to Version",
    value: "version",
    sortable: true,
  },
  {
    title: "Enabled",
    value: "enabled",
    sortable: true,
  },
  { title: "Actions", value: "actions" },
];

const architecture = [
  { title: "Apple Silicon", value: "AppleSilicon" },
  { title: "Intel", value: "Intel" },
];

const appsById = computed(() => {
  let result = [];
  // Loop through the apps and creata new array of objects with id and name id must be unique
  props.apps.forEach((app) => {
    let match = result.find((a) => a.id == app.id);
    if (!match) {
      result.push({
        id: app.id,
        name: app.name,
        stream: app.stream,
      });
    }
  });

  // Filter by search term if provided
  if (addRuleSearch.value) {
    const searchLower = addRuleSearch.value.toLowerCase();
    result = result.filter((app) =>
      app.name.toLowerCase().includes(searchLower)
    );
  }

  return result;
});

const showAddRule = ref(false);
const addAppHeaders = [{ title: "Application", value: "name" }];
const addRuleSearch = ref("");

function addAppClick(item, row) {
  showAddRule.value;
  //console.log("Model Start", model);
  // Check if model value is an array and if it is not, initialize it
  if (!Array.isArray(model.value)) {
    console.warn("Model value is not an array, initializing it.");
    model.value = [];
  }
  //console.log("model.value", model.value);
  model.value.push({
    id: row.item.id,
    name: row.item.name,
    stream: "",
    version: "",
    enabled: true,
  });
  showAddRule.value = false;
}

function availableArchitectures(appId) {
  let result = [];
  props.apps.forEach((app) => {
    if (app.id == appId) {
      result.push({
        id: app.stream,
        name: app.stream,
      });
    }
  });
  return result;
}

function addRule() {
  //console.log("Add Rule clicked");
  showAddRule.value = true;
}

function imageURL(appID) {
  var result = "";
  if (appID) {
    result = "https://patch24content.j24.io/" + appID + ".png";
  }
  return result;
}

function deleteRule(index) {
  //console.log("Delete Rule clicked", index);
  model.value.splice(index, 1);
}
</script>

<template>
  <v-overlay
    v-model="showAddRule"
    class="align-center justify-center"
    min-width="600"
  >
    <v-card closable>
      <v-card-title>
        <v-row>
          <v-col> Add App Rule </v-col>
          <v-spacer></v-spacer>

          <v-col cols="auto">
            <v-btn @click="showAddRule = false" color="primary">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <v-text-field
              label="Search..."
              variant="plain"
              hide-details
              v-model="addRuleSearch"
            ></v-text-field>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-virtual-scroll :items="appsById" :height="400">
              <template v-slot:default="{ item }">
                <v-hover v-slot="{ isHovering, props }">
                  <v-row
                    v-bind="props"
                    align="center"
                    class="pointer ma-0"
                    :class="{ 'bg-grey-lighten-3': isHovering }"
                    @click="addAppClick(item, { item })"
                  >
                    <v-col cols="auto">
                      <AppIcon :appID="item.id" />
                    </v-col>
                    <v-col cols="auto">
                      {{ item.name }}
                    </v-col>
                  </v-row>
                </v-hover>
              </template>
            </v-virtual-scroll>

            <!-- <v-data-table
              :headers="addAppHeaders"
              :items="appsById"
              hide-default-header
              :search="addRuleSearch"
              @click:row="addAppClick"
              :items-per-page="500"
              :hide-default-footerzx="false"
            >
              <template v-slot:[`item.name`]="{ item }">
                <v-row align="center">
                  <v-col cols="auto">
                    <AppIcon :appID="item.id" />
                  </v-col>
                  <v-col cols="auto">
                    {{ item.name }}
                  </v-col>
                </v-row>
              </template>
            </v-data-table> -->
          </v-col>
        </v-row>

        <!-- <v-row>
          <v-col cols="12">
            <v-select-search
              :items="appsById"
              v-model="addRuleSearch"
              label="Select App"
              item-text="name"
              item-value="id"
            ></v-select-search>
          </v-col>
        </v-row> -->
      </v-card-text>
    </v-card>
  </v-overlay>

  <v-row>
    <v-col cols="auto">
      <div class="font-weight-bold">App Specific Rules</div>
      <div class="text-body-2 font-weight-light text-medium-emphasis">
        Specific rules for individual applications. An application can be
        configured to update only to a specified version.
      </div>
    </v-col>
    <v-spacer></v-spacer>

    <v-col cols="auto" v-if="props.group">
      <v-switch
        label="Inherit Rules"
        color="primary"
        baseColor="grey-darken-2"
        v-model="inheritAppRules"
        :readonly="props.readOnlyMode"
      ></v-switch>
    </v-col>
    <v-col cols="auto" v-if="!props.readOnlyMode">
      <v-btn
        prepend-icon="mdi-plus"
        :loading="props.loading"
        color="primary"
        base-color="grey-darken-2"
        @click="addRule"
        >Add Rule</v-btn
      >
    </v-col>
  </v-row>
  <v-row v-if="!inheritAppRules">
    <v-col cols="12">
      <v-data-table :headers="headers" :items="model">
        <template v-slot:[`item.name`]="{ item }">
          <v-row align="center">
            <v-col cols="auto">
              <AppIcon :appID="item.id" />
            </v-col>
            <v-col cols="auto">
              {{ item.name }}
            </v-col>
          </v-row>
        </template>

        <template v-slot:[`item.stream`]="{ item }">
          <v-select
            :items="architecture"
            v-model="item.stream"
            label="Select Architecture"
            item-text="title"
            item-value="value"
            hide-details
            variant="outlined"
          ></v-select>
        </template>

        <template v-slot:[`item.version`]="{ item }">
          <AppRuleVersionPicker
            v-model="item.version"
            :appId="item.id"
            :stream="item.stream"
          ></AppRuleVersionPicker>
        </template>

        <template v-slot:[`item.enabled`]="{ item }">
          <v-switch
            :label="item.enabled ? 'On' : 'Off'"
            color="primary"
            base-color="grey-darken-2"
            v-model="item.enabled"
          ></v-switch>
        </template>

        <template v-slot:[`item.actions`]="{}">
          <!-- <v-btn icon @click="editRule(item)" variant="plain">
            <v-icon>mdi-pencil</v-icon>
          </v-btn> -->
          <v-btn icon @click="deleteRule(index)" variant="plain">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>
